/**
 * Session state for the panel.
 *
 * AUTHORIZATION IS CAPABILITIES, NOT RANK (D-032 §4.0). `can()` comes from
 * `@navoiy/domain` and is the ONLY thing this app may branch on. Comparing roles
 * by their position in `admin_role_t` would hand `content_editor` — whose job is
 * the mahalla directory — read access to citizens' phone numbers, addresses and
 * appeal bodies, purely because it is declared after `handler` in the enum.
 *
 * The server enforces every one of these gates independently. The client-side
 * check exists so staff are not shown buttons that can only 403.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { can, type AdminCapability } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';

export type AdminMe = components['schemas']['AdminMe'];

type Status = 'loading' | 'authenticated' | 'anonymous';

interface SessionContextValue {
  status: Status;
  me: AdminMe | null;
  /** Capability gate — never compare `role` directly. */
  allows: (capability: AdminCapability) => boolean;
  /**
   * `totp` is optional: an account with no enrolled authenticator signs in with
   * email and password alone (migration 0018). Pass the raw field value —
   * blank is normalised away here rather than at every call site.
   */
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Called by screens when any request 401s, so the panel returns to login. */
  expire: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<AdminMe | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = unwrap(await api().GET('/admin/me'));
        if (!cancelled) {
          setMe(data);
          setStatus('authenticated');
        }
      } catch {
        // 401 here is the normal cold-start case, not an error worth surfacing.
        if (!cancelled) {
          setMe(null);
          setStatus('anonymous');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    // OMITTED, never sent empty. `AdminLoginRequest.totp` keeps its
    // `^[0-9]{6}$` pattern whenever the field is present, so `totp: ''` is a
    // 422 from the contract validator — a different, louder failure than the
    // undifferentiated 401 the login screen is built around, and it would hit
    // every password-only account on every sign-in.
    const code = totp?.trim();
    const data = unwrap(
      await api().POST('/admin/auth/login', {
        body: { email, password, ...(code ? { totp: code } : {}) },
      }),
    );
    setMe(data);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api().POST('/admin/auth/logout');
    } catch (error) {
      // A failed logout must still clear the client; the cookie is revoked
      // server-side on the next authenticated call regardless.
      if (!(error instanceof ApiError)) throw error;
    }
    setMe(null);
    setStatus('anonymous');
  }, []);

  const expire = useCallback(() => {
    setMe(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      me,
      allows: (capability) => (me ? can(me.role, capability) : false),
      login,
      logout,
      expire,
    }),
    [status, me, login, logout, expire],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession used outside SessionProvider');
  return context;
}
